import React from "react";
import { NavLink } from "react-router-dom";
import { Button, Typography } from "@material-ui/core";

import { trackEventEditRenewal, trackEventTurnRenewalOn } from "utils/gAnalyticsUtils";
import { appRoutes, externalRoutes } from "routes";
import { useUsers, useNotifications } from "hooks";
import Placeholder from "components/Placeholder";
import AccountLayout from "layouts/AccountLayout";
import UserPresenter from "presenters/UserPresenter";
import SubscriptionPresenter from "presenters/SubscriptionPresenter";
import MetaTags from "components/MetaTags";

import { SectionHeader } from "../components";

import useStyles from "./useStyles";

const SubscriptionDetails = () => {
  const classes = useStyles();
  const { addSuccessNotification } = useNotifications();
  const { currentUser, reactivateSubscription } = useUsers();
  const subscription = UserPresenter.activeSubscription(currentUser);
  const isPaid = SubscriptionPresenter.isPaid(subscription);
  const isGift = SubscriptionPresenter.gifted(subscription);
  const isApple = SubscriptionPresenter.isApple(subscription);
  const isAmazon = SubscriptionPresenter.isAmazon(subscription);
  const isPayPal = SubscriptionPresenter.isPayPal(subscription);
  const isRecurly = SubscriptionPresenter.isRecurly(subscription);

  const renewalMessage = () => {
    const endDate = SubscriptionPresenter.endAtDisplayDate(subscription);
    let type = isPaid ? "renew" : "expire";

    if (isApple) {
      type = "renew or expire";
    }

    return `Your membership will ${type} on ${endDate}.`;
  };

  const billingMessage = () => {
    if (isGift) {
      return "You will not be charged for your membership.";
    }

    let type = "Credit Card";

    if (isPayPal) {
      type = "PayPal account";
    } else if (isApple) {
      type = "Apple account";
    } else if (isAmazon) {
      type = "Amazon account";
    }

    return `Your membership payments will be charged to your ${type}.`;
  };

  const showBilling = () => {
    return isGift || isApple || isPaid;
  };

  const RenewalButton = () => {
    const showButton = () => {
      const status = SubscriptionPresenter.status(subscription);
      const processing = status.indexOf("process") >= 0 || status === "created";
      const recurlyEnabled = !processing && (isRecurly || isAmazon);
      const paypalEnabled = isPayPal && (isPaid || SubscriptionPresenter.isPaused(subscription));
      const androidEnabled = isPaid && SubscriptionPresenter.isAndroid(subscription);

      return paypalEnabled || recurlyEnabled || androidEnabled;
    };

    const turnOnAutoRenewal = () => {
      const subId = SubscriptionPresenter.id(subscription);
      const params = {
        data: {
          type: "reactivations",
        },
      };

      reactivateSubscription(subId, params).then(() => {
        trackEventTurnRenewalOn();
        addSuccessNotification("Auto-renew Enabled");
      });
    };

    if (!showButton()) {
      return null;
    }

    return (
      <>
        {isPaid ? (
          <NavLink exact className={classes.link} to={appRoutes.editRenewalPath()}>
            <Button variant="contained" color="primary" className={classes.button} onClick={trackEventEditRenewal}>
              Edit Renewal
            </Button>
          </NavLink>
        ) : (
          <Button variant="contained" color="primary" className={classes.button} onClick={turnOnAutoRenewal}>
            Turn Renewal On
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <AccountLayout
        title="Subscription eCards - Customize & Send Funny Greeting Cards Online - JibJab "
        backLink={appRoutes.myAccountPath()}
      >
        {currentUser ? (
          <>
            <SectionHeader first>Membership Description</SectionHeader>
            <Typography className={classes.text}>
              {isGift ? "Gift Subscription" : SubscriptionPresenter.offerDescription(subscription)}
            </Typography>

            <SectionHeader>Renewal</SectionHeader>
            <Typography className={classes.text}>{renewalMessage()}</Typography>

            <RenewalButton />

            {showBilling() && (
              <>
                <SectionHeader>Billing Info</SectionHeader>
                <div className={classes.row}>
                  <Typography className={classes.billingInfo}>
                    {billingMessage()}
                    {isApple && (
                      <>
                        Visit our{" "}
                        <a href={externalRoutes.helpPath()} rel="noreferrer" target="_blank">
                          Help Center
                        </a>{" "}
                        for more information on managing Apple subscriptions.
                      </>
                    )}
                  </Typography>

                  {isPaid && isRecurly && (
                    <NavLink exact className={classes.link} to={appRoutes.updateCreditCardPath()}>
                      Edit Payment Details
                    </NavLink>
                  )}
                </div>
              </>
            )}

            <Typography variant="caption" display="block" className={classes.captionText}>
              If you have any questions regarding this membership, please{" "}
              <a href={externalRoutes.helpPath()} rel="noreferrer" target="_blank">
                visit our Support Center for Help
              </a>
              .
            </Typography>
          </>
        ) : (
          <>
            <SectionHeader first>Membership Description</SectionHeader>
            <Placeholder variant="text" className={classes.placeholder} />

            <SectionHeader>Renewal</SectionHeader>
            <Placeholder variant="text" className={classes.placeholder} />

            <SectionHeader>Billing Info</SectionHeader>
            <Placeholder variant="text" className={classes.placeholder} />
          </>
        )}
      </AccountLayout>
      <MetaTags>
        <meta
          name="og:description"
          content="Personalized eCards from JibJab for every occasion. Browse, customize, send funny greeting cards online for birthdays, holidays, valentines, thank yous & more!"
        />
      </MetaTags>
    </>
  );
};

export default SubscriptionDetails;
